import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';
import { seoArticles } from '@/mocks/seo-articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.tattoo';

export default function SeoArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = seoArticles.find((a) => a.slug === slug);

  const articleSchema = useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.headline,
      description: article.metaDescription,
      image: article.image,
      datePublished: '2026-08-10',
      dateModified: '2026-08-10',
      author: {
        '@type': 'Organization',
        name: 'Basetao Sheet',
        url: siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Basetao Sheet',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/article/${article.slug}`,
      },
    };
  }, [article]);

  const faqSchema = useMemo(() => {
    if (!article || article.faqs.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = article.pageTitle;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">Article Not Found</h1>
            <p className="text-foreground-600 text-sm mb-6">The article you are looking for does not exist or has been moved.</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-all"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      {articleSchema && <JsonLd data={articleSchema as Record<string, unknown>} />}
      {faqSchema && <JsonLd data={faqSchema as Record<string, unknown>} />}
      <Navbar />

      <main className="pt-20 md:pt-24">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <a href="/#seo-articles" className="hover:text-primary-600 transition-colors">Guides &amp; Articles</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{article.headline}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wide uppercase mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.headline}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              {article.intro}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Updated August 2026</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
            </div>
          </header>

          {/* Article Image */}
          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.headline}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Body */}
          <div>
            {article.sections.map((section, sIdx) => (
              <section key={sIdx} className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {section.title}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4">{para}</p>
                ))}
                {section.subItems && (
                  <div className="space-y-3 mt-4">
                    {section.subItems.map((item, iIdx) => (
                      <div key={iIdx} className="flex gap-3 bg-background-100 rounded-lg p-4 border border-background-200/60">
                        <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                          <span className="text-xs font-bold">{iIdx + 1}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground-900 text-sm mb-1">{item.title}</h4>
                          <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* Internal CTA */}
            <div className="bg-primary-50 border border-primary-200/50 rounded-xl p-5 mb-10">
              <p className="text-foreground-800 text-sm leading-relaxed">
                <i className="ri-lightbulb-line text-primary-600 mr-1.5"></i>
                <strong>Pro Tip:</strong> Bookmark this guide and explore more resources in our <a href="/#seo-articles" className="text-primary-600 hover:text-primary-700 underline font-medium">Basetao Guides section</a>. The more you learn before ordering, the smoother your shopping experience will be.
              </p>
            </div>

            {/* FAQ Section */}
            {article.faqs.length > 0 && (
              <section className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, fIdx) => (
                    <div key={fIdx} className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Conclusion */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {article.conclusionTitle}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">{article.conclusion}</p>
              <p className="text-foreground-700 leading-relaxed">
                Ready to put this knowledge into practice? Head over to the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline font-medium">main Basetao platform</a> to start exploring products and building your ultimate shopping database today.
              </p>
            </section>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary-400/10 to-accent-400/10 rounded-xl p-6 md:p-8 border border-primary-200/30 mb-10">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">Ready to Start Shopping?</h3>
              <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                Access the full Basetao Spreadsheet database and discover thousands of curated finds organized by category, price, and quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-external-link-line"></i>
                  Explore Basetao Spreadsheet
                </a>
                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-foreground-900 text-background-50 hover:bg-foreground-800"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Homepage
                </a>
              </div>
            </div>

            {/* Related Articles */}
            <div className="border-t border-background-200/60 pt-8">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Related Resources</h3>
              <div className="flex flex-wrap gap-2">
                <a href="/" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-home-4-line"></i> Homepage
                </a>
                {seoArticles.filter(a => a.slug !== slug).slice(0, 5).map((a) => (
                  <a
                    key={a.slug}
                    href={`/article/${a.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-article-line"></i> {a.headline.split('—')[0].trim().substring(0, 40)}
                  </a>
                ))}
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-all whitespace-nowrap">
                  <i className="ri-external-link-line"></i> Visit Main Website
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}